import {
  Breadcrumbs,
  Card,
  Typography,
  Grid,
  Input,
  Stack,
  FormLabel,
  Box,
  CircularProgress,
  Chip,
} from '@mui/joy';
import { CreateEditAccountModal } from '@/components/Pages/Accounts/CreateEditAccountModal';
import { DeleteAccountModal } from '@/components/Pages/Accounts/DeleteAccountModal';
import { createFileRoute, Link, Navigate, useRouter } from '@tanstack/react-router';
import { pageTitle, usePageTitle } from '@/utils/pageTitle';
import { LedgerTable } from '@/components/Pages/Accounts/LedgerTable';
import { useAccount } from '@/hooks/useAccounts';
import { useAuth } from '@/contexts/AuthContext';

export const Route = createFileRoute('/_protected/accounts/$id')({
  component: AccountDetail,
  head: () => pageTitle('Cuentas'),
});

function AccountDetail() {
  const { id } = Route.useParams();
  const router = useRouter();
  const { hasClaim, isLoading: authLoading } = useAuth();
  const canRead = hasClaim('ACCOUNTS_READ');
  const canWrite = hasClaim('ACCOUNTS_WRITE');
  const canDelete = hasClaim('ACCOUNTS_DELETE');

  const { data: account, isLoading } = useAccount(id);
  usePageTitle('Cuentas', account?.name);

  if (authLoading || isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!canRead) {
    return <Navigate to='/accounts' />;
  }

  if (!account) {
    return <div>Cuenta no encontrada</div>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Stack direction='row' justifyContent='space-between'>
        <Breadcrumbs size='sm'>
          <Link to='/accounts'>Cuentas</Link>
          <Stack direction='row' alignItems='center' gap={1} flexWrap='wrap'>
            <Typography>{account?.name}</Typography>
            {!account.active && (
              <Chip size='sm' color='danger' variant='soft'>
                Eliminado
              </Chip>
            )}
          </Stack>
        </Breadcrumbs>

        {(canWrite || canDelete) && (
          <Stack direction='row' gap={1} alignItems='center'>
            {canWrite && (
              <CreateEditAccountModal
                account={account}
                disabled={!account.active}
              />
            )}
            {canDelete && (
              <DeleteAccountModal
                disabled={!account.active}
                id={String(account.id)}
                name={account.name}
                onSuccess={() => router.navigate({ to: '/accounts' })}
              />
            )}
          </Stack>
        )}
      </Stack>

      <Card>
        <Grid container spacing={{ xs: 1, sm: 2 }}>
          <Grid xs={12} md={6}>
            <FormLabel>Nombre</FormLabel>
            <Input
              value={account?.name}
              readOnly
              variant='soft'
              size='sm'
              sx={{ backgroundColor: 'background.body' }}
            />
          </Grid>
          <Grid xs={12} md={6}>
            <FormLabel>Saldo Total</FormLabel>
            <Input
              value={account?.totalAmount.toLocaleString('es-AR', {
                minimumFractionDigits: 2,
                style: 'currency',
                currency: 'ARS',
              })}
              readOnly
              variant='soft'
              size='sm'
              sx={theme => ({
                backgroundColor: 'background.body',
                fontWeight: 'bold',
                '& input': {
                  color:
                    account?.totalAmount > 0
                      ? theme.vars.palette.success.plainColor
                      : theme.vars.palette.danger.plainColor,
                },
              })}
            />
          </Grid>
        </Grid>
      </Card>

      <Card>
        <Box sx={{ mb: 2 }}>
          <Typography level='h4'>Movimientos</Typography>
        </Box>

        <LedgerTable
          initialFilters={{ accountId: id }}
          emptyMessage='No hay movimientos registrados'
          errorTitle='Error al cargar los movimientos'
        />
      </Card>
    </Box>
  );
}
